import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BellRinging, CheckCircle } from '@phosphor-icons/react';
import { ROLE_LABEL, hasMailChannel, type SessionUser } from '@helpdesk/shared';
import { ApiRequestError, getJson, patchJson, postJson } from '../api/client.ts';
import { meQuery } from '../api/queries.ts';
import { confirmLogout, endSession, logout } from '../components/layout.tsx';
import { ChangePasswordForm } from '../components/password.tsx';
import { Button, Card, Checkbox, ErrorNote, Notice, PageTitle, Spinner, errorMessage } from '../components/ui.tsx';
import { pushState, subscribePush, unsubscribePush, type PushState } from '../lib/push.ts';
import { useAction } from '../lib/use-action.ts';

type Profile = {
  user: SessionUser;
  notifyEmail: boolean;
  notifyPush: boolean;
  sites: Array<{ id: string; code: string; name: string; source: string }>;
  pushAvailable: boolean;
  subscriptions: Array<{ id: string; userAgent: string | null; createdAt: string; lastOkAt: string | null }>;
};

const SESSIONS_UNSURE = 'Ob die Sitzungen beendet wurden, ist nicht sicher.';

/** Profil (F34): Benachrichtigungen, Push auf diesem Gerät, Sitzungen. */
export function ProfilePage() {
  const me = useQuery(meQuery);
  const qc = useQueryClient();
  const profile = useQuery({ queryKey: ['profile'], queryFn: () => getJson<Profile>('/api/profile') });
  const [push, setPush] = useState<PushState | 'failed' | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const notifications = useAction();
  // Eigener Fehlerzustand: Am Knopf „Überall abmelden“ läge die Karte „Benachrichtigungen“ außer Sicht.
  const sessions = useAction();
  const account = me.data?.user;
  const vapidKey = me.data?.vapidPublicKey ?? null;

  useEffect(() => {
    if (account) void pushState(account, vapidKey).then(setPush, () => setPush('failed'));
  }, [account, vapidKey]);

  if (profile.isPending) return <Spinner />;
  if (profile.isError) return <ErrorNote error={profile.error} />;
  const p = profile.data;
  const refresh = () => qc.invalidateQueries({ queryKey: ['profile'] });

  // Die Rückfrage kommt zuerst: Danach ist die Sitzung schon beendet, und ein Abbruch ließe die Seite ohne Anmeldung stehen.
  const logoutEverywhere = () =>
    sessions.run(async () => {
      if (!(await confirmLogout(p.user.id))) return;
      try {
        await postJson('/api/profile/end-all-sessions');
      } catch (err) {
        const code = err instanceof ApiRequestError ? err.code : undefined;
        if (code !== 'unauthorized') {
          // Nur unsere eigene Fehlerform heißt „abgelehnt“. Antwortet ein Proxy oder niemand, kann der Server die Sitzungen trotzdem beendet haben.
          const refused = code && code !== 'unreadable_response';
          throw new Error(`${refused ? 'Die Sitzungen wurden nicht beendet.' : SESSIONS_UNSURE} ${errorMessage(err)}`, { cause: err });
        }
        // Die Sitzung dieses Geräts ist weg, etwa weil nur die Antwort auf den ersten Klick verloren ging. Ob andere noch laufen, sagt das nicht, und die Seite danach zeigt keine Meldung mehr.
        window.alert(`${SESSIONS_UNSURE} Dieses Gerät ist bereits abgemeldet. Zur Sicherheit bitte neu anmelden und „Überall abmelden“ wiederholen.`);
      }
      await endSession();
    });

  const notify = (action: () => Promise<void>) => {
    setInfo(null);
    return notifications.run(action);
  };

  const setPref = (body: { notifyEmail?: boolean; notifyPush?: boolean }) =>
    notify(async () => {
      await patchJson('/api/profile', body);
      await refresh();
    });

  return (
    <div className="mx-auto max-w-[760px] space-y-5">
      <PageTitle>Profil</PageTitle>
      <Card title={p.user.displayName}>
        <p className="text-[15px]">
          {p.user.authSource === 'local' ? 'Konto ohne Microsoft 365' : p.user.email} · {ROLE_LABEL[p.user.role]}
          {p.user.isSysadmin ? ' · Systemverwaltung' : ''}
        </p>
        <p className="mt-1 text-[15px]">
          Standorte:{' '}
          {p.sites.length ? (
            p.sites.map((s) => `${s.name}${s.source === 'override' && p.user.authSource === 'entra' ? ' (Ausnahme)' : ''}`).join(', ')
          ) : p.user.role === 'technician' ? (
            <strong className="text-warn">Kein Standort zugeordnet. Bitte an die Systemverwaltung wenden.</strong>
          ) : p.user.role === 'lead' ? (
            'Alle Werke'
          ) : (
            '-'
          )}
        </p>
      </Card>

      <Card title="Benachrichtigungen">
        <div className="space-y-4">
          <div className="flex flex-col gap-1">
            {hasMailChannel(p.user) ? (
              <Checkbox label="per E-Mail" checked={p.notifyEmail} disabled={notifications.busy} onChange={(v) => void setPref({ notifyEmail: v })} />
            ) : (
              <p className="text-[15px]">Ihr Konto bekommt keine Mails. Neues sehen Sie als Push und im Ticket.</p>
            )}
            <Checkbox label="als Push aufs Handy oder Tablet" checked={p.notifyPush} disabled={notifications.busy} onChange={(v) => void setPref({ notifyPush: v })} />
          </div>

          <div className="border-t border-[var(--border-default)] pt-4">
            <p className="mb-2 font-semibold text-steel-900">Push auf diesem Gerät</p>
            {!p.pushAvailable ? (
              <Notice>Push ist auf dem Server noch nicht eingerichtet (VAPID-Schlüssel fehlen).</Notice>
            ) : push === 'ios-install' ? (
              <Notice>Auf dem iPhone gibt es Push nur in der installierten App: unten auf „Teilen“ tippen, dann „Zum Home-Bildschirm“. Danach die App vom Home-Bildschirm öffnen und hier einschalten.</Notice>
            ) : push === 'unsupported' ? (
              <Notice>Dieser Browser kann keine Push-Nachrichten. Bitte Chrome, Edge oder Safari verwenden.</Notice>
            ) : push === 'denied' ? (
              <Notice tone="warn">Benachrichtigungen sind im Browser für diese Seite blockiert. Bitte in den Website-Einstellungen des Browsers erlauben und die Seite neu laden.</Notice>
            ) : push === 'subscribed' ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="mr-2 inline-flex items-center gap-1.5 font-medium text-ok">
                  <CheckCircle size={22} weight="fill" /> Aktiv
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={notifications.busy}
                  onClick={() =>
                    void notify(async () => {
                      const res = await postJson<{ delivered: number }>('/api/profile/push-test');
                      setInfo(res.delivered ? 'Test gesendet. Die Nachricht sollte gleich erscheinen.' : 'Kein Gerät erreicht. Bitte Push hier aus- und wieder einschalten.');
                    })
                  }
                >
                  Test senden
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={notifications.busy}
                  onClick={() =>
                    void notify(async () => {
                      await unsubscribePush();
                      setPush(await pushState(p.user, vapidKey));
                      await refresh();
                    })
                  }
                >
                  Ausschalten
                </Button>
              </div>
            ) : push === 'available' ? (
              <Button
                disabled={notifications.busy}
                onClick={() =>
                  void notify(async () => {
                    const ok = await subscribePush(p.user.id, vapidKey!);
                    setPush(await pushState(p.user, vapidKey));
                    if (!ok) setInfo('Benachrichtigungen wurden im Browser nicht erlaubt.');
                    await refresh();
                  })
                }
              >
                <BellRinging size={22} />
                Einschalten
              </Button>
            ) : push === 'failed' ? (
              <Notice tone="warn">Der Push-Status dieses Geräts ließ sich nicht abfragen. Bitte die Seite neu laden.</Notice>
            ) : null}
          </div>
          {info ? <Notice>{info}</Notice> : null}
          {notifications.error ? <ErrorNote error={notifications.error} /> : null}
          <p className="text-[14px] text-[var(--text-muted)]">Push-Nachrichten enthalten nur Nummer, Maschine, Priorität, Status und einen festen Hinweis zum Anlass, keinen Freitext.</p>
        </div>
      </Card>

      {p.user.authSource === 'local' ? (
        <Card title="Passwort">
          <ChangePasswordForm />
        </Card>
      ) : null}

      <Card title="Sitzungen">
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" disabled={sessions.busy} onClick={() => void logoutEverywhere()}>
            Überall abmelden
          </Button>
          <Button variant="secondary" onClick={() => void logout(p.user.id)}>
            Abmelden
          </Button>
        </div>
        {sessions.error ? (
          <div className="mt-3">
            <ErrorNote error={sessions.error} />
          </div>
        ) : null}
      </Card>
      <p className="text-[13px] text-[var(--text-muted)]">Version {me.data?.version}</p>
    </div>
  );
}
